import React, { useState, useEffect, useMemo } from 'react';

import Axios from 'axios';

import styled from 'styled-components';

import { isBookingEvent } from '@libs/EventTypes';
import { getMoment, showPeriodNoTime, toISODateTime } from '@libs/NwMoment';

import { useContactsSelector } from '@components/ContactsSelector/Common/ContactsSelectorContext';
import ModelAvailabilityDrawerEvent from '@components/ContactsSelector/ModelsSelector/ModelsAvailability/ModelAvailabilityDrawerEvent';
import NwDrawer from '@components/Gui/NwDrawer';

const DrawerContentWrapper = styled.div`
    padding: 0 1rem;

    .event-month {
        margin-bottom: 1rem;

        h2 {
            font-size: 1.2rem;
            font-weight: 400;
            margin-bottom: 0.5rem;
            text-transform: uppercase;
            color: ${(props) => props.theme.TextColorMuted};
        }

        .event-month-days {
            .event-day {
                margin-bottom: 1rem;

                h3 {
                    font-size: 1.2rem;
                    font-weight: 600;
                    margin-bottom: 0.5rem;
                }
            }
        }
    }
`;

const ModelAvailabilityIconDrawer = ({ modelId, onClose }) => {
    const [modelEvents, setModelEvents] = useState([]);
    const [isFetchedEvents, setFetchedEvents] = useState(false);

    const {
        cache: { modelAvailability },
    } = useContactsSelector();

    const fetchEvents = async (startdate, enddate) => {
        setFetchedEvents(false);
        const start = toISODateTime(getMoment(startdate).startOf('day'));
        const end = toISODateTime(getMoment(enddate).endOf('day'));
        let newValues = {
            FDate: start,
            TDate: end,
            idmodels: [modelId],
            inclJobs: true,
            inclExtJobs: true,
            inclCastings: true,
            checkConflict: true,
            inclTravels: true,
            inclAccommodations: true,
            inclExternalJobs: true,
            inclFollowUps: true,
            inclMeetings: true,
            inclNotices: true,
            General: false,
            Warning: false,
        };
        Axios.post('/calendar/getevents', newValues)
            .then((modelagenda) => {
                setModelEvents(modelagenda.Events);
                setFetchedEvents(true);
            })
            .catch((error) => {
                //TODO: manage error on api call, may be by adding an ErrorMessage to the state and a condition to show it in render.
                console.error('error', error);
            });
    };

    useEffect(() => {
        if (modelAvailability && modelAvailability.dates && modelAvailability.dates.length > 0) {
            const startdate = modelAvailability.dates[0];
            const enddate = modelAvailability.dates[modelAvailability.dates.length - 1];
            fetchEvents(startdate, enddate);
        }
    }, [modelAvailability]);

    const eventsGroupedByDay = useMemo(() => {
        const groupedByMonth = {};
        // if (!dates || !dates.startdate || !dates.enddate || !modelEvents || modelEvents.length === 0) return [];

        if (
            !modelEvents ||
            modelEvents.length === 0 ||
            !modelAvailability ||
            !modelAvailability.dates ||
            modelAvailability.dates.length === 0
        )
            return [];

        const periodStart = getMoment(modelAvailability.dates[0]);
        const periodEnd = getMoment(modelAvailability.dates[modelAvailability.dates.length - 1]);

        modelEvents.forEach((event) => {
            const startDate = getMoment(event.FromDate);
            const endDate = getMoment(event.ToDate);

            if (!isBookingEvent(event.TypeName) && !startDate.isSame(endDate, 'day')) {
                let currentDate = startDate.clone();
                if (currentDate.isBefore(periodStart, 'day')) {
                    currentDate = periodStart.clone();
                }
                const loopEndDate = endDate.isAfter(periodEnd) ? periodEnd.clone() : endDate.clone();
                while (currentDate.isSameOrBefore(loopEndDate, 'day')) {
                    const monthYear = currentDate.format('MMMM YYYY');
                    const dateStr = currentDate.format('YYYY-MM-DD');
                    if (!groupedByMonth[monthYear]) {
                        groupedByMonth[monthYear] = {
                            month: currentDate.format('MMMM'),
                            year: currentDate.year(),
                            dates: {},
                        };
                    }
                    if (!groupedByMonth[monthYear].dates[dateStr]) {
                        groupedByMonth[monthYear].dates[dateStr] = {
                            date: dateStr,
                            events: [],
                        };
                    }
                    groupedByMonth[monthYear].dates[dateStr].events.push({
                        ...event,
                        isMultiDayEvent: true,
                        multiDayPosition: currentDate.isSame(startDate, 'day')
                            ? 'start'
                            : currentDate.isSame(endDate, 'day')
                              ? 'end'
                              : 'middle',
                    });
                    currentDate.add(1, 'day');
                }
            } else {
                const eventDate = startDate;
                if (eventDate.isBetween(periodStart, periodEnd, 'day', '[]')) {
                    const monthYear = eventDate.format('MMMM YYYY');
                    const dateStr = eventDate.format('YYYY-MM-DD');
                    if (!groupedByMonth[monthYear]) {
                        groupedByMonth[monthYear] = {
                            month: eventDate.format('MMMM'),
                            year: eventDate.year(),
                            dates: {},
                        };
                    }
                    if (!groupedByMonth[monthYear].dates[dateStr]) {
                        groupedByMonth[monthYear].dates[dateStr] = {
                            date: dateStr,
                            events: [],
                        };
                    }
                    groupedByMonth[monthYear].dates[dateStr].events.push(event);
                }
            }
        });

        const periodDates = Object.values(groupedByMonth).map((monthData) => ({
            ...monthData,
            dates: Object.values(monthData.dates),
        }));

        const isInModelAvailabilityDates = (date) => {
            if (modelAvailability.isSingleDay) {
                return modelAvailability.dates[0].substring(0, 10) === date.substring(0, 10);
            }
            return modelAvailability.dates.includes(date);
        };
        const availabilityDates = periodDates.map((monthData) => {
            const filteredDates = monthData.dates.filter((dateData) => isInModelAvailabilityDates(dateData.date));
            return {
                ...monthData,
                dates: filteredDates,
            };
        });
        return availabilityDates;
    }, [modelEvents, modelAvailability]);

    return (
        <NwDrawer
            title={
                modelAvailability
                    ? showPeriodNoTime(
                          modelAvailability.dates[0],
                          modelAvailability.dates[modelAvailability.dates.length - 1]
                      )
                    : ''
            }
            open
            onClose={(e) => {
                if (e) {
                    e.stopPropagation();
                }
                onClose();
            }}
        >
            <div>
                {eventsGroupedByDay.length > 0 ? (
                    eventsGroupedByDay.map((monthData) => (
                        <DrawerContentWrapper>
                            <div key={`${monthData.month}-${monthData.year}`} className="event-month">
                                {eventsGroupedByDay.length > 1 && (
                                    <h2>
                                        {monthData.month} {monthData.year}
                                    </h2>
                                )}
                                <div className="event-month-days">
                                    {monthData.dates.map((dateData) => (
                                        <div key={dateData.date} className="event-day">
                                            <h3>{getMoment(dateData.date).format('D MMMM YYYY')}</h3>
                                            <div className="event-day-events">
                                                {dateData.events.map((event) => {
                                                    return (
                                                        <ModelAvailabilityDrawerEvent
                                                            key={`${event.TypeName}-${event.ID}`}
                                                            event={event}
                                                            currentDate={dateData.date}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </DrawerContentWrapper>
                    ))
                ) : (
                    <p>no events</p>
                )}
            </div>
        </NwDrawer>
    );
};

export default ModelAvailabilityIconDrawer;
